import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Beef, Sun, HeartPulse } from 'lucide-react';

const HealthBenefits = () => {
  const benefits = [
    {
      icon: Heart,
      title: 'Immune Boosting',
      description: 'Rich in beta-glucans and antioxidants that strengthen your immune system naturally.',
      color: 'text-red-500',
      bgColor: 'bg-red-50',
    },
    {
      icon: Beef,
      title: 'High Protein',
      description: 'Excellent plant-based protein source, perfect for vegetarian and vegan diets.',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
    },
    {
      icon: Sun,
      title: 'Vitamin D Rich',
      description: 'Naturally high in vitamin D, essential for bone health and immune function.',
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-50',
    },
    {
      icon: HeartPulse,
      title: 'Zero Cholesterol',
      description: 'Heart-healthy choice with zero cholesterol and low saturated fat content.',
      color: 'text-forest-600',
      bgColor: 'bg-forest-50',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section id="health" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
            Health & Science
          </h2>
          <div className="w-24 h-1 bg-forest-600 mx-auto mb-6"></div>
          <p className="text-xl text-stone-600 max-w-3xl mx-auto">
            Discover the scientifically-proven health benefits of incorporating organic mushrooms into your diet.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.05 }}
                className={`${benefit.bgColor} rounded-2xl p-8 text-center glass hover:shadow-xl transition-shadow`}
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 ${benefit.bgColor} rounded-full mb-4 border-2 border-current ${benefit.color}`}>
                  <Icon className={`w-8 h-8 ${benefit.color}`} />
                </div>
                <h3 className="text-xl font-semibold text-stone-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-stone-600 leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16 bg-gradient-to-r from-forest-600 to-forest-700 rounded-3xl p-8 md:p-12 text-white text-center"
        >
          <h3 className="text-3xl font-bold mb-4">
            Science-Backed Nutrition
          </h3>
          <p className="text-lg text-forest-50 max-w-3xl mx-auto leading-relaxed">
            Our mushrooms are cultivated using methods that maximize their nutritional profile. Research shows that organic cultivation methods can enhance the concentration of beneficial compounds, making our produce not just delicious, but truly nourishing for your body.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default HealthBenefits;
